import { AdjustableCropperBackground } from './AdjustableCropperBackground'
import React, { useEffect, useRef, useState } from 'react'
import { Cropper, CropperRef } from 'react-mobile-cropper'
import Modal from 'components/Modal/Modal'

import 'react-mobile-cropper/dist/style.css'
import './index.scss'

interface Photo {
  name: string
  type: string
  stringSrc: string
}

interface PhotoEditorModalProps {
  photo: File | Photo
  setPhoto: (file: File) => void
  close: base.BasicFunc
}

type AdjustmentKey = 'brightness' | 'saturation' | 'contrast' | 'hue' | 'sepia'
type Adjustments = Record<AdjustmentKey, number>

interface Input {
  name: AdjustmentKey
  label: string
  min: number
  max: number
  type: 'range'
  id: string
  ariaLabelledby: string
}

const inputs: Input[] = [{
  name: 'brightness',
  label: 'Jasność',
  min: -100,
  max: 100,
  type: 'range',
  id: 'brightnessInput',
  ariaLabelledby: 'brightnessInputLabel',
}, {
  name: 'contrast',
  label: 'Kontrast',
  min: -100,
  max: 100,
  type: 'range',
  id: 'contrastInput',
  ariaLabelledby: 'contrastInputLabel',
}, {
  name: 'saturation',
  label: 'Saturacja',
  min: -100,
  max: 100,
  type: 'range',
  id: 'saturationInput',
  ariaLabelledby: 'saturationInputLabel',
}, {
  name: 'hue',
  label: 'Barwa',
  min: -100,
  max: 100,
  type: 'range',
  id: 'hueInput',
  ariaLabelledby: 'hueInputLabel',
}, {
  name: 'sepia',
  label: 'Sepia',
  min: 0,
  max: 100,
  type: 'range',
  id: 'sepiaInput',
  ariaLabelledby: 'sepiaInputLabel',
}]

const initalAdjustments = {
  brightness: 0,
  saturation: 0,
  contrast: 0,
  hue: 0,
  sepia: 0
}

const PhotoEditorModal: React.FC<PhotoEditorModalProps> = ({ photo, setPhoto, close }) => {
  const [image, setImage] = useState('')
  const [thereAreDetails, showDetails] = useState(false)

  const [adjustments, setAdjustments] = useState<Adjustments>(initalAdjustments)

  const cropperRef = useRef<CropperRef>()

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: AdjustmentKey,
    min: number,
    max: number
  ) => {
    const value = parseInt(event.target?.value)
    if (!isNaN(value) && value >= min && value <= max) {
      setAdjustments((previousValue) => ({
        ...previousValue,
        [name]: value,
      }))
    }
  }

  const saveImage = () => {
    const cropper = cropperRef.current
    if (cropper) {
      const canvas = cropper.getCanvas()
      canvas?.toBlob((blob) => {
        if (blob) {
          const editedFile = new File([blob], photo.name, { type: photo.type })
          setPhoto(editedFile)
        }
        close()
      })
    }
  }

  useEffect(() => {
    if (photo) {
      const isCustomPhoto = 'stringSrc' in photo
      const _image = isCustomPhoto ? photo.stringSrc : URL.createObjectURL(photo)
      setImage(_image)
      return () => {
        if (!isCustomPhoto) {
          URL.revokeObjectURL(_image)
        }
      }
    }
  }, [photo])

  return (
    <Modal
      title='Edycja'
      onClose={close}
      primaryButton='Zapisz'
      primaryButtonAction={() => {
        saveImage()
      }}
      secondaryButton='Anuluj'
      secondaryButtonAction={() => {
        close()
      }}>
      <div className='photoEditor'>
        <Cropper
          src={image}
          ref={cropperRef}
          backgroundProps={adjustments}
          backgroundComponent={AdjustableCropperBackground}
          className='photoEditor__cropper'
          stencilProps={{
            aspectRatio: 179 / 193,
            resizable: false
          }}
        />

        <div className='photoEditor__buttons'>
          <button
            className='photoEditor__button --settings'
            onClick={() => {
              showDetails(prev => !prev)
            }}>
            <i className='icon icon--settings' />
          </button>
          <button
            className='photoEditor__button --blackAndWhite'
            onClick={() => {
              setAdjustments(
                adjustments.brightness === 0
                  && adjustments.contrast === 0
                  && adjustments.hue === 0
                  && adjustments.saturation === -100
                  && adjustments.sepia === 0
                  ? initalAdjustments
                  : {
                    brightness: 0,
                    saturation: -100,
                    contrast: 0,
                    hue: 0,
                    sepia: 0
                  })
            }}>
          </button>
          <button
            className='photoEditor__button --sepia'
            onClick={() => {
              setAdjustments(
                adjustments.brightness === 0
                  && adjustments.contrast === 0
                  && adjustments.hue === 0
                  && adjustments.saturation === -1
                  && adjustments.sepia === 100
                  ? initalAdjustments
                  : {
                    brightness: 0,
                    saturation: -1,
                    contrast: 0,
                    hue: 0,
                    sepia: 100
                  })
            }}>
          </button>
        </div>

        {thereAreDetails && <div className='photoEditor__options'>
          <i
            className='icon icon--close'
            onClick={() => {
              showDetails(false)
            }} />
          {inputs.map((input) => <div
            key={input.name}
            className='photoEditor__option'>
            <label
              id={`${input.name}InputLabel`}
              className='photoEditor__option__label'>
              {input.label}:
            </label>
            <input
              id={input.id}
              min={input.min}
              max={input.max}
              type={input.type}
              className='photoEditor__option__input --range'
              aria-labelledby={input.ariaLabelledby}
              value={adjustments[input.name]}
              step='1'
              onChange={(e) => {
                handleInputChange(e, input.name, input.min, input.max)
              }}
            />
            <input
              type='number'
              min={input.min}
              max={input.max}
              className='photoEditor__option__input --number'
              aria-labelledby={input.ariaLabelledby}
              value={adjustments[input.name]}
              onChange={(e) => {
                handleInputChange(e, input.name, input.min, input.max)
              }}
            />
          </div>)}
        </div>}
      </div>
    </Modal>
  );
};

export default PhotoEditorModal
